import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

/** Must match the boot script in public/index.html. */
export const THEME_STORAGE_KEY = 't1care.theme';
export const THEME_PREFERENCES = ['light', 'dark', 'system'];
const DEFAULT_PREFERENCE = 'light';

/** Browser chrome color per resolved theme (light keeps the original brand blue). */
const META_THEME_COLOR = { light: '#1976d2', dark: '#0f172a' };
const STATUS_BAR_COLOR = { light: '#ffffff', dark: '#1e293b' };

const ThemeContext = createContext(null);

export function isValidPreference(value) {
  return THEME_PREFERENCES.includes(value);
}

export function readStoredPreference() {
  try {
    const v = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isValidPreference(v) ? v : DEFAULT_PREFERENCE;
  } catch {
    return DEFAULT_PREFERENCE;
  }
}

function writeStoredPreference(value) {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, value);
  } catch {
    /* storage unavailable (private mode) */
  }
}

function systemPrefersDark() {
  try {
    return Boolean(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
  } catch {
    return false;
  }
}

export function resolveTheme(preference, prefersDark) {
  if (preference === 'dark') return 'dark';
  if (preference === 'system') return prefersDark ? 'dark' : 'light';
  return 'light';
}

function applyDocumentTheme(resolved) {
  const root = document.documentElement;
  if (root.dataset.theme !== resolved) root.dataset.theme = resolved;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', META_THEME_COLOR[resolved]);
}

async function applyNativeStatusBar(resolved) {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    // Style.Dark = light icons (for dark backgrounds); Style.Light = dark icons.
    await StatusBar.setStyle({ style: resolved === 'dark' ? Style.Dark : Style.Light });
    if (Capacitor.getPlatform() === 'android') {
      await StatusBar.setBackgroundColor({ color: STATUS_BAR_COLOR[resolved] });
    }
  } catch (err) {
    console.warn('Status bar theme update failed:', err);
  }
}

export function ThemeProvider({ children }) {
  const { user, profile } = useAuth();
  const [preference, setPreferenceState] = useState(readStoredPreference);
  const [prefersDark, setPrefersDark] = useState(systemPrefersDark);
  const hydratedUidRef = useRef(null);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => setPrefersDark(e.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else if (mql.addListener) mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else if (mql.removeListener) mql.removeListener(onChange);
    };
  }, []);

  // Keep other tabs in sync.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === THEME_STORAGE_KEY && isValidPreference(e.newValue)) setPreferenceState(e.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Adopt the saved profile preference once per signed-in user (it follows them across devices).
  useEffect(() => {
    if (!user || !profile) {
      if (!user) hydratedUidRef.current = null;
      return;
    }
    if (hydratedUidRef.current === user.uid) return;
    hydratedUidRef.current = user.uid;
    const remote = profile.uiPreferences?.theme;
    if (isValidPreference(remote)) {
      setPreferenceState(remote);
      writeStoredPreference(remote);
    }
  }, [user, profile]);

  const resolvedTheme = resolveTheme(preference, prefersDark);

  useLayoutEffect(() => {
    applyDocumentTheme(resolvedTheme);
  }, [resolvedTheme]);

  useEffect(() => {
    applyNativeStatusBar(resolvedTheme);
  }, [resolvedTheme]);

  const setPreference = useCallback(
    async (next) => {
      if (!isValidPreference(next)) return;
      setPreferenceState(next);
      writeStoredPreference(next);
      if (!user?.uid) return;
      try {
        await updateDoc(doc(db, 'users', user.uid), { 'uiPreferences.theme': next });
      } catch (err) {
        console.warn('Saving theme preference failed:', err);
      }
    },
    [user],
  );

  const value = useMemo(() => ({ preference, resolvedTheme, setPreference }), [preference, resolvedTheme, setPreference]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
